import { ArcwareInit } from '@arcware-cloud/pixelstreaming-websdk';
import * as Sentry from '@sentry/react';

// Add new event types for connection issues
export interface ConnectionIssue {
  type: 'websocket_closed' | 'afk_timeout' | 'afk_warning' | 'afk_warning_update';
  code?: number;
  reason?: string;
  message?: string;
  warningData?: any; // For AFK warning update data
}

export interface PixelStreamingHandlerOptions {
  shareId: string;
  containerElement: HTMLElement;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
  onConnectionIssue?: (issue: ConnectionIssue) => void; // New handler for connection issues
  onQueueStatusChange?: (inQueue: boolean, message?: string) => void; // New handler for queue status
  initialSettings?: {
    StartVideoMuted?: boolean;
    AutoConnect?: boolean;
    AutoPlayVideo?: boolean;
    [key: string]: any;
  };
  uiSettings?: {
    infoButton?: boolean;
    micButton?: boolean;
    audioButton?: boolean;
    fullscreenButton?: boolean;
    settingsButton?: boolean;
    connectionStrengthIcon?: boolean;
    [key: string]: any;
  };
}

// Arcware close codes from documentation
export enum ArcwareCloseCode {
  UNAUTHORIZED = 4450,
  UNAUTHORIZED_NO_BYPASS_REGISTERED = 4451,
  SUSPENDED = 4453,
  NO_STREAM_AVAILABLE = 4501,
  NO_PACKAGENAME_PROVIDED = 4502,
  STREAM_DISCONNECTED = 4503,
  STREAM_KEY_ALREADY_CONNECTED = 4504,
  CLIENT_OCCUPATION_LIMIT_REACHED = 4505,
  CLIENT_AFK = 4506,
  CLIENT_RECONNECTING = 4507,
  CLIENT_NO_PONG = 4508,
  CLIENT_DESTROYED = 4509,
  CLOSE_ABNORMAL_CUSTOM = 4606,
  CLOSE_SHARE_NO_RESOURCES_LEFT = 4607,
  CLOSE_SHARE_ERROR = 4608,
  CLOSE_INTERNAL_SERVER_ERROR = 4666
}

export class PixelStreamingHandler {
  private application: any = null;
  private pixelStreaming: any = null;
  private options: PixelStreamingHandlerOptions;
  private resizeTimeout: NodeJS.Timeout | null = null;
  private isInQueue: boolean = false;
  
  constructor(options: PixelStreamingHandlerOptions) {
    console.log('PixelStreamingHandler: Constructor called with options:', options);
    this.options = options;
  }
  
  async initialize(): Promise<void> {
    return Sentry.startSpan({
      op: 'pixelstreaming.init',
      name: 'PixelStreaming SDK Initialization'
    }, async (span: any) => {
      span.setAttribute('shareId', this.options.shareId);
      span.setAttribute('autoConnect', this.options.initialSettings?.AutoConnect ?? false);
      
      try {
        console.log('PixelStreamingHandler: Initializing with shareId:', this.options.shareId);
      
      const configWithShareId = { shareId: this.options.shareId };
      const settingsConfig = {
        initialSettings: {
          StartVideoMuted: this.options.initialSettings?.StartVideoMuted ?? false,
          AutoConnect: this.options.initialSettings?.AutoConnect ?? false,
          AutoPlayVideo: this.options.initialSettings?.AutoPlayVideo ?? false,
          ...this.options.initialSettings
        },
        settings: {
          infoButton: this.options.uiSettings?.infoButton ?? true,
          micButton: this.options.uiSettings?.micButton ?? true,
          audioButton: this.options.uiSettings?.audioButton ?? true,
          fullscreenButton: this.options.uiSettings?.fullscreenButton ?? true,
          settingsButton: this.options.uiSettings?.settingsButton ?? true,
          connectionStrengthIcon: this.options.uiSettings?.connectionStrengthIcon ?? true,
          ...this.options.uiSettings
        },
      };
      
      console.log('PixelStreamingHandler: Calling ArcwareInit with config:', configWithShareId);
      console.log('PixelStreamingHandler: Calling ArcwareInit with settings (AutoConnect should be false):', settingsConfig);
      
      const { Config, PixelStreaming, Application } = ArcwareInit(
        configWithShareId,
        settingsConfig
      );
      
      console.log('PixelStreamingHandler: ArcwareInit returned:', { Config, PixelStreaming, Application });
      
      this.application = Application;
      this.pixelStreaming = PixelStreaming;
      
      // Override SDK's global font styles after initialization
      this.overrideSDKGlobalStyles();
      
      // Setup connection state and AFK event listeners
      this.setupConnectionHandlers();
      this.setupAFKHandlers();
      this.setupQueueHandlers();
      this.setupResizeHandlers();
      
      if (this.options.containerElement) {
        if (this.application && this.application.rootElement) {
          console.log('PixelStreamingHandler: Appending rootElement to container');
          this.options.containerElement.appendChild(this.application.rootElement);
          console.log('PixelStreamingHandler: rootElement appended successfully.');
        } else {
          console.error('PixelStreamingHandler: application.rootElement is not available');
          throw new Error('PixelStreaming root element not available');
        }
      } else {
        console.error('PixelStreamingHandler: Container element is not available');
        throw new Error('Container element not available');
      }
      
      } catch (error) {
        console.error('PixelStreamingHandler: Failed to initialize PixelStreaming:', error);
        const sentryError = error instanceof Error ? error : new Error('Initialization error');
        Sentry.captureException(sentryError, {
          extra: {
            shareId: this.options.shareId,
            containerAvailable: !!this.options.containerElement,
            initialSettings: this.options.initialSettings
          },
          tags: {
            component: 'pixelstreaming_handler',
            operation: 'initialize'
          }
        });
        this.options.onError?.(sentryError);
        throw error;
      }
    });
  }

  private setupConnectionHandlers(): void {
    if (!this.pixelStreaming) return;

    // Setup WebSocket close event handler
    if (typeof this.pixelStreaming.websocketOnCloseHandler?.add === 'function') {
      this.pixelStreaming.websocketOnCloseHandler.add((event: CloseEvent) => {
        console.log(`PixelStreamingHandler: WebSocket closed. CloseCode: ${event.code} Reason: ${event.reason}`);
        
        // Capture WebSocket closure as Sentry event
        Sentry.captureMessage('PixelStreaming WebSocket Closed', {
          level: event.code >= 4000 ? 'error' : 'warning',
          extra: {
            closeCode: event.code,
            reason: event.reason,
            arcwareCodeDescription: this.getArcwareCodeDescription(event.code),
            shareId: this.options.shareId
          },
          tags: {
            component: 'pixelstreaming_handler',
            operation: 'websocket_close'
          }
        });
        
        if (this.options.onConnectionIssue) {
          let message = 'Connection closed';
          
          // Handle Arcware-specific close codes
          switch (event.code) {
            case ArcwareCloseCode.UNAUTHORIZED:
              message = 'Unauthorized access - please check your credentials';
              break;
            case ArcwareCloseCode.NO_STREAM_AVAILABLE:
              message = 'No stream available - please try again later';
              break;
            case ArcwareCloseCode.STREAM_DISCONNECTED:
              message = 'Stream disconnected unexpectedly';
              break;
            case ArcwareCloseCode.CLIENT_AFK:
              message = 'Connection closed due to inactivity';
              break;
            case ArcwareCloseCode.CLIENT_OCCUPATION_LIMIT_REACHED:
              message = 'Maximum number of users reached - please try again later';
              break;
            case ArcwareCloseCode.CLOSE_SHARE_NO_RESOURCES_LEFT:
              message = 'No resources available - please try again later';
              break;
            case ArcwareCloseCode.CLOSE_INTERNAL_SERVER_ERROR:
              message = 'Internal server error - please try again later';
              break;
            default:
              if (event.reason) {
                message = `Connection closed: ${event.reason}`;
              }
          }

          this.options.onConnectionIssue({
            type: 'websocket_closed',
            code: event.code,
            reason: event.reason,
            message
          });
        }
      });
    } else {
      console.warn('PixelStreamingHandler: websocketOnCloseHandler not available');
    }
  }

  private setupAFKHandlers(): void {
    if (!this.pixelStreaming) return;

    // Setup AFK event listeners - Handle all AFK events from Arcware/Epic SDK
    if (typeof this.pixelStreaming.addEventListener === 'function') {
      // AFK Warning Activate - when the warning popup appears (typically at ~120s)
      this.pixelStreaming.addEventListener('afkWarningActivate', () => {
        console.log('PixelStreamingHandler: AFK Warning Activated - Warning popup appeared');
        
        // Capture AFK warning in Sentry
        Sentry.captureMessage('PixelStreaming AFK warning activated', {
          level: 'info',
          extra: {
            shareId: this.options.shareId,
            timestamp: Date.now()
          },
          tags: {
            component: 'pixelstreaming_handler',
            operation: 'afk_warning_activate'
          }
        });
        
        if (this.options.onConnectionIssue) {
          this.options.onConnectionIssue({
            type: 'afk_warning',
            message: 'You appear to be inactive. Please interact with the stream to continue.'
          });
        }
      });

      // AFK Warning Deactivate - when user becomes active again
      this.pixelStreaming.addEventListener('afkWarningDeactivate', () => {
        console.log('PixelStreamingHandler: AFK Warning Deactivated - User became active again');
        
        // Note: We don't need to handle this in UI as the warning overlay will be dismissed
        // when user interacts, but we can log it for debugging
      });

      // AFK Warning Update - periodic updates during warning period
      this.pixelStreaming.addEventListener('afkWarningUpdate', (data: any) => {
        console.log('PixelStreamingHandler: AFK Warning Update:', data);
        
        if (this.options.onConnectionIssue) {
          this.options.onConnectionIssue({
            type: 'afk_warning_update',
            message: 'Please interact with the stream to avoid disconnection.',
            warningData: data
          });
        }
      });

      // AFK Timeout - final timeout when user gets disconnected
      this.pixelStreaming.addEventListener('afkTimedOut', () => {
        console.log('PixelStreamingHandler: AFK Timeout - User disconnected due to inactivity');
        
        // Capture AFK timeout in Sentry
        Sentry.captureMessage('PixelStreaming AFK timeout occurred', {
          level: 'warning',
          extra: {
            shareId: this.options.shareId,
            timestamp: Date.now()
          },
          tags: {
            component: 'pixelstreaming_handler',
            operation: 'afk_timeout'
          }
        });
        
        if (this.options.onConnectionIssue) {
          this.options.onConnectionIssue({
            type: 'afk_timeout',
            message: 'Connection closed due to inactivity. Please refresh the page to reconnect.'
          });
        }
      });
    } else {
      console.warn('PixelStreamingHandler: addEventListener not available for AFK events');
    }
  }

  private setupQueueHandlers(): void {
    if (!this.pixelStreaming) return;

    // Setup Arcware queue handler
    if (this.pixelStreaming.queueHandler && typeof this.pixelStreaming.queueHandler.add === 'function') {
      this.pixelStreaming.queueHandler.add((message: any) => {
        console.log('PixelStreamingHandler: Queue message received:', message);
        this.isInQueue = true;
        
        // Format queue information for display
        let messageText = '';
        if (typeof message === 'string') {
          messageText = message;
        } else if (message && typeof message === 'object') {
          // Handle Arcware queue object structure
          if (message.index !== undefined && message.queueLength !== undefined) {
            const position = message.index + 1; // Convert 0-based index to 1-based position
            const total = message.queueLength;
            const waitTime = message.estimatedWaitTime || message.averageWaitTime;
            
            messageText = `Position ${position} von ${total} in der Warteschlange.`;
            
            if (waitTime && waitTime > 0) {
              const minutes = Math.ceil(waitTime / 60);
              messageText += ` Geschätzte Wartezeit: ${minutes} Minute${minutes !== 1 ? 'n' : ''}.`;
            }
          } else {
            // Fallback for other object structures
            messageText = message.message || message.text || 'Sie befinden sich in der Warteschlange.';
          }
        }
        
        console.log('PixelStreamingHandler: Formatted queue message:', messageText);
        
        // Notify the component about queue status
        if (this.options.onQueueStatusChange) {
          this.options.onQueueStatusChange(true, messageText);
        }
      });
      console.log('PixelStreamingHandler: Queue handler set up successfully');
    } else {
      console.warn('PixelStreamingHandler: queueHandler not available on PixelStreaming object');
    }
  }

  private setupResizeHandlers(): void {
    console.log('PixelStreamingHandler: Setting up minimal resize handlers');
    
    // Let Arcware SDK handle resize detection internally
    // We'll only intervene if absolutely necessary
  }

  public isCurrentlyInQueue(): boolean {
    return this.isInQueue;
  }



  public async connect(): Promise<void> {
    return Sentry.startSpan({
      op: 'pixelstreaming.connect',
      name: 'PixelStreaming Connection'
    }, async (span: any) => {
      span.setAttribute('shareId', this.options.shareId);
      span.setAttribute('isInQueue', this.isInQueue);
      
      if (!this.pixelStreaming) {
        const err = new Error('PixelStreaming SDK not initialized or pixelStreaming object not available.');
        console.error('PixelStreamingHandler:', err.message);
        Sentry.captureException(err, {
          extra: { shareId: this.options.shareId },
          tags: { component: 'pixelstreaming_handler', operation: 'connect' }
        });
        this.options.onError?.(err);
        throw err;
      }
      try {
        console.log('PixelStreamingHandler: Attempting to connect via SDK\'s connect method...');
      
      // Reset queue status when attempting to connect
      this.isInQueue = false;
      if (this.options.onQueueStatusChange) {
        this.options.onQueueStatusChange(false);
      }
      
      await this.pixelStreaming.connect(); // Calling the SDK's connect method
      console.log('PixelStreamingHandler: SDK connect method called. Assuming connection will proceed.');
      // The SDK should now handle the connection.
      // If the SDK has a direct callback or promise for successful connection, that would be ideal.
      // For now, we will trigger our onConnect callback here, assuming the SDK's connect initiates the process
      // and that subsequent SDK events (if properly wired) would handle actual state changes.
      // This is a temporary placement for onConnect if specific SDK connected event isn't available for immediate use.
      this.options.onConnect?.(); 
      } catch (error) {
        console.error('PixelStreamingHandler: Error during SDK connect:', error);
        const sentryError = error instanceof Error ? error : new Error('SDK Connection Error');
        Sentry.captureException(sentryError, {
          extra: {
            shareId: this.options.shareId,
            isInQueue: this.isInQueue,
            activeInstances: this.getActiveInstances(),
            websocketStates: this.getWebsocketStates()
          },
          tags: {
            component: 'pixelstreaming_handler',
            operation: 'connect'
          }
        });
        this.options.onError?.(sentryError);
        throw error;
      }
    });
  }
  
  sendEvent(eventName: string, eventData: any): void {
    Sentry.startSpan({
      op: 'pixelstreaming.event',
      name: `Send Event: ${eventName}`
    }, (span: any) => {
      span.setAttribute('eventName', eventName);
      span.setAttribute('shareId', this.options.shareId);
      span.setAttribute('applicationReady', !!this.application);
      
      if (!this.application) {
        console.warn('PixelStreamingHandler: Application not initialized');
        Sentry.captureMessage('PixelStreaming application not initialized for event', {
          level: 'warning',
          extra: { eventName, eventData, shareId: this.options.shareId },
          tags: { component: 'pixelstreaming_handler', operation: 'send_event' }
        });
        return;
      }
      
      try {
        console.log(`PixelStreamingHandler: Sending event '${eventName}' with data:`, eventData);
        this.application.emitUIInteraction({
          type: eventName,
          data: eventData
        });
        console.log(`PixelStreamingHandler: Event '${eventName}' sent successfully`);
      } catch (error) {
        console.error(`PixelStreamingHandler: Failed to send event '${eventName}':`, error);
        Sentry.captureException(error instanceof Error ? error : new Error(`Failed to send event: ${eventName}`), {
          extra: {
            eventName,
            eventData,
            shareId: this.options.shareId
          },
          tags: {
            component: 'pixelstreaming_handler',
            operation: 'send_event'
          }
        });
      }
    });
  }

  
  disconnect(): void {
    console.log('PixelStreamingHandler: Disconnect called');
    if (this.pixelStreaming) { // Check if pixelStreaming object exists
        this.pixelStreaming.disconnect(); // Call SDK's disconnect
        console.log('PixelStreamingHandler: SDK disconnect method called.');
    }
    if (this.application && this.application.rootElement && this.application.rootElement.parentNode) {
      console.log('PixelStreamingHandler: Removing rootElement from DOM');
      try {
        this.application.rootElement.parentNode.removeChild(this.application.rootElement);
        console.log('PixelStreamingHandler: rootElement removed.');
      } catch (error) {
        // NotFoundError can occur if React has already unmounted the element
        // or if the Arcware SDK has cleaned up the DOM before us
        console.log('PixelStreamingHandler: rootElement already removed from DOM (race condition handled)');
      }
    }
    this.application = null;
    this.pixelStreaming = null;
    console.log('PixelStreamingHandler: Application cleanup complete');
    // Manually trigger onDisconnect if not covered by SDK events for now
    // This is a fallback. Proper SDK event handling for disconnect is preferred.
    this.options.onDisconnect?.();
  }

  public destroy(): void {
    console.log('PixelStreamingHandler: Destroying handler and cleaning up all resources');
    this.disconnect();
    this.cleanup();
  }

  public getApplicationResponse(callback: (response: any) => void): void {
    if (this.application && typeof this.application.getApplicationResponse === 'function') {
      this.application.getApplicationResponse(callback);
    } else {
      console.warn('PixelStreamingHandler: Application or getApplicationResponse is not available');
    }
  }

  public onVideoInitialized(callback: () => void): void {
    if (this.pixelStreaming && this.pixelStreaming.videoInitializedHandler) {
      this.pixelStreaming.videoInitializedHandler.add(callback);
      console.log('PixelStreamingHandler: Added callback to videoInitializedHandler');
    } else {
      console.warn('PixelStreamingHandler: PixelStreaming object or videoInitializedHandler not available to add callback.');
    }
  }

  public updateContainerElement(containerElement: HTMLElement): void {
    console.log('PixelStreamingHandler: Updating container element');
    this.options.containerElement = containerElement;
  }

  public getVideoElement(): HTMLVideoElement | null {
    if (!this.application?.rootElement) {
      console.warn('PixelStreamingHandler: Application or rootElement not available');
      return null;
    }
    return this.application.rootElement.querySelector('video');
  }

  // sendMessage was not in the original, but if it exists, keep it.
  // public sendMessage(message: any): void { ... }

  public getWebsocketStates(): any[] | null {
    if (this.pixelStreaming && typeof this.pixelStreaming.WebsocketStates !== 'undefined') {
      return this.pixelStreaming.WebsocketStates;
    }
    console.warn('PixelStreamingHandler: pixelStreaming object not available or WebsocketStates getter not found.');
    return null;
  }

  public getActiveInstances(): number | null {
    if (this.pixelStreaming && typeof this.pixelStreaming.ActiveInstances !== 'undefined') {
      return this.pixelStreaming.ActiveInstances;
    }
    console.warn('PixelStreamingHandler: pixelStreaming object not available or ActiveInstances getter not found.');
    return null;
  }


  private overrideSDKGlobalStyles(): void {
    console.log('PixelStreamingHandler: Removing SDK global font styles');
    
    // Use a timeout to ensure SDK styles are fully applied before we modify them
    setTimeout(() => {
      // Find and modify the SDK-injected style sheets
      const styleSheets = Array.from(document.styleSheets);
      
      for (const styleSheet of styleSheets) {
        try {
          // Check if this stylesheet contains the problematic "*" rule
          const rules = Array.from(styleSheet.cssRules || []);
          
          for (let i = rules.length - 1; i >= 0; i--) {
            const rule = rules[i] as CSSStyleRule;
            
            // Look for the global "*" selector with font-family
            if (rule.selectorText === '*' && rule.style.fontFamily) {
              console.log('PixelStreamingHandler: Found and removing SDK global font rule:', rule.cssText);
              styleSheet.deleteRule(i);
            }
          }
        } catch (e) {
          // StyleSheet might be cross-origin, skip it
          console.log('PixelStreamingHandler: Could not access stylesheet (likely cross-origin)');
        }
      }
      
      // Also apply targeted overrides for specific elements that might still be affected
      const overrideStyles = `
        /* Restore font inheritance for specific host page elements */
        .elementor-button-content-wrapper,
        .elementor-heading-title {
          font-family: inherit !important;
        }
        
        /* Ensure our banner elements use intended fonts */
        .plaza-banner__title {
          font-family: var(--e-global-typography-primary-font-family, "ivymode-variable"), Sans-serif !important;
          font-weight: 600 !important;
        }
        
        .plaza-banner__subtitle {
          font-family: var(--e-global-typography-accent-font-family, var(--font-family-text)) !important;
        }
        
        .plaza-banner__button {
          font-family: var(--e-global-typography-accent-font-family, var(--font-family-text)) !important;
          font-size: 18px !important;
          font-weight: 500 !important;
          letter-spacing: 0rem !important;
        }
        
        /* Ensure PixelStreaming SDK elements maintain their fonts */
        #playerUI, #playerUI * {
          font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        }
        
        #uiFeatures, #uiFeatures * {
          font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        }
        
        #controls, #controls * {
          font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        }
      `;
      
      const styleElement = document.createElement('style');
      styleElement.textContent = overrideStyles;
      styleElement.setAttribute('data-plaza-banner-sdk-override', 'true');
      document.head.appendChild(styleElement);
      
      console.log('PixelStreamingHandler: SDK font override prevention completed');
    }, 100); // Small delay to ensure SDK styles are applied first
  }

  public cleanup(): void {
    console.log('PixelStreamingHandler: Cleaning up resources');
    
    // Clean up any timeouts
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
    
    // Clean up our SDK style overrides
    const styleOverride = document.querySelector('style[data-plaza-banner-sdk-override]');
    if (styleOverride) {
      styleOverride.remove();
      console.log('PixelStreamingHandler: SDK style override removed');
    }
    
    console.log('PixelStreamingHandler: Cleanup completed');
  }

  private getArcwareCodeDescription(code: number): string {
    switch (code) {
      case ArcwareCloseCode.UNAUTHORIZED: return 'Unauthorized access';
      case ArcwareCloseCode.NO_STREAM_AVAILABLE: return 'No stream available';
      case ArcwareCloseCode.STREAM_DISCONNECTED: return 'Stream disconnected';
      case ArcwareCloseCode.CLIENT_AFK: return 'Client AFK timeout';
      case ArcwareCloseCode.CLIENT_OCCUPATION_LIMIT_REACHED: return 'Occupation limit reached';
      case ArcwareCloseCode.CLOSE_SHARE_NO_RESOURCES_LEFT: return 'No resources left';
      case ArcwareCloseCode.CLOSE_INTERNAL_SERVER_ERROR: return 'Internal server error';
      default: return 'Unknown close code';
    }
  }
} 